/**
 * BabyCareStatsPage
 *
 * Detailed statistics page for baby care activities.
 * Includes time range selection with tier-based restrictions.
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Lock, Calendar, ChevronDown, Baby } from 'lucide-react';
import { createPortal } from 'react-dom';
import { useTranslation } from '@parentpath/i18n';
import { usePregnancyStore } from '@/store/pregnancyStore';
import { useFeatureEnabled } from '@/hooks/useFeatureFlags';
import { useLimits } from '@/hooks/useLimits';
import { FeatureDisabled } from '@/components/FeatureDisabled';
import { isValidUUID } from '@/lib/utils';
import { getBabyCareEntries, BabyCareEntry } from '@/services/babyCareService';
import { getBabiesFromPregnancy, hasMultipleBabies } from '@/lib/babyUtils';
import { BabyCareStatsTimeline } from '../components/BabyCareStatsTimeline';
import { BabyCareCharts } from '../components/BabyCareCharts';

// Time range options
const TIME_RANGES = [
  { value: 1, labelKey: 'statsPage.timeRanges.today', days: 1 },
  { value: 7, labelKey: 'statsPage.timeRanges.7days', days: 7 },
  { value: 14, labelKey: 'statsPage.timeRanges.14days', days: 14 },
  { value: 30, labelKey: 'statsPage.timeRanges.30days', days: 30 },
  { value: 180, labelKey: 'statsPage.timeRanges.custom', days: 180 },
] as const;

type TimeRangeValue = typeof TIME_RANGES[number]['value'];

export function BabyCareStatsPage() {
  const { t } = useTranslation('babyCare');
  const { lastCompletedPregnancy, appMode } = usePregnancyStore();
  const isBabyCareEnabled = useFeatureEnabled('baby_care_enabled');
  const { getNumberLimit, userTier } = useLimits();

  // State
  const [selectedRange, setSelectedRange] = useState<TimeRangeValue>(1);
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [showRangeDropdown, setShowRangeDropdown] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [entries, setEntries] = useState<BabyCareEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedBabyIndex, setSelectedBabyIndex] = useState<number | null>(null);

  // Multi-child support
  const babies = getBabiesFromPregnancy(lastCompletedPregnancy);
  const isMultiChild = hasMultipleBabies(lastCompletedPregnancy);

  // Filter entries by selected baby
  const filteredEntries = selectedBabyIndex !== null
    ? entries.filter(e => e.baby_index === selectedBabyIndex)
    : entries;

  // Validate pregnancy ID
  const rawPregnancyId = lastCompletedPregnancy?.id;
  const pregnancyId = isValidUUID(rawPregnancyId) ? rawPregnancyId : null;

  // Check mode
  const wasBirth = lastCompletedPregnancy?.completion_type === 'birth';
  const isBabyMode = appMode === 'baby' && wasBirth;

  // Get max allowed days from limits
  const maxAllowedDays = getNumberLimit('baby_care_stats_max_days');

  // Check if a time range is available
  const isRangeAvailable = useCallback((rangeValue: number): boolean => {
    return rangeValue <= maxAllowedDays;
  }, [maxAllowedDays]);

  // Fetch stats based on selected range
  const fetchStats = useCallback(async () => {
    if (!pregnancyId || !isBabyCareEnabled) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    try {
      // Calculate start date for entries fetch
      let startDate: string | undefined;
      if (selectedRange === 180 && customStartDate) {
        startDate = new Date(customStartDate).toISOString();
      } else {
        const start = new Date();
        start.setDate(start.getDate() - selectedRange);
        startDate = start.toISOString();
      }

      const entriesResult = await getBabyCareEntries(pregnancyId, { startDate, limit: 500 });

      if (!entriesResult.error) {
        setEntries(entriesResult.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setIsLoading(false);
    }
  }, [pregnancyId, isBabyCareEnabled, selectedRange, customStartDate, customEndDate]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Get current range label
  const currentRangeLabel = t(TIME_RANGES.find(r => r.value === selectedRange)?.labelKey || 'statsPage.timeRanges.today');

  // Handle range selection
  const handleRangeSelect = (value: TimeRangeValue) => {
    if (!isRangeAvailable(value)) return;

    setSelectedRange(value);
    setShowRangeDropdown(false);

    if (value === 180) {
      // Show date picker for custom range
      const defaultStart = new Date();
      defaultStart.setDate(defaultStart.getDate() - 30);
      setCustomStartDate(defaultStart.toISOString().split('T')[0]);
      setShowDatePicker(true);
    } else {
      setCustomStartDate('');
      setShowDatePicker(false);
    }
  };

  // Show disabled state if feature is not enabled
  if (!isBabyCareEnabled) {
    return (
      <FeatureDisabled
        featureName={t('statsPage.featureDisabled.name')}
        description={t('statsPage.featureDisabled.description')}
      />
    );
  }

  // Show message if not in baby mode
  if (!isBabyMode || !pregnancyId) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4">
          <Baby className="w-8 h-8 text-white/40" />
        </div>
        <h2 className="text-white font-medium text-lg mb-2">{t('statsPage.title')}</h2>
        <p className="text-white/60 text-sm max-w-xs mx-auto">
          {t('statsPage.notAvailable')}
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <h1 className="text-white text-xl font-semibold">{t('statsPage.title')}</h1>
      </div>

      {/* Time Range Selector */}
      <div className="mb-6">
        <div className="relative">
          <button
            onClick={() => setShowRangeDropdown(!showRangeDropdown)}
            className="w-full glass-effect rounded-[10px] px-4 py-3 flex items-center justify-between text-white"
          >
            <span className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-white/60" />
              <span>{currentRangeLabel}</span>
              {selectedRange === 180 && customStartDate && (
                <span className="text-white/60 text-sm">
                  ({customStartDate} - {customEndDate})
                </span>
              )}
            </span>
            <ChevronDown className={`w-5 h-5 transition-transform ${showRangeDropdown ? 'rotate-180' : ''}`} />
          </button>

          {/* Dropdown */}
          {showRangeDropdown && (
            <div className="absolute top-full left-0 right-0 mt-2 glass-effect rounded-[10px] overflow-hidden z-10">
              {TIME_RANGES.map((range) => {
                const isAvailable = isRangeAvailable(range.value);
                return (
                  <button
                    key={range.value}
                    onClick={() => isAvailable && handleRangeSelect(range.value)}
                    disabled={!isAvailable}
                    className={`w-full px-4 py-3 flex items-center justify-between text-left transition-colors ${
                      selectedRange === range.value
                        ? 'bg-pp-orange text-white'
                        : isAvailable
                        ? 'text-white hover:bg-white/10'
                        : 'text-white/40 cursor-not-allowed'
                    }`}
                  >
                    <span>{t(range.labelKey)}</span>
                    {!isAvailable && (
                      <Lock className="w-4 h-4 text-pp-orange" />
                    )}
                  </button>
                );
              })}

              {/* Upsell hint */}
              {userTier === 'free' && (
                <div className="px-4 py-3 border-t border-white/10">
                  <Link
                    to="/subscription"
                    className="text-pp-orange text-sm hover:underline"
                  >
                    {t('statsPage.premiumHint')}
                  </Link>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Baby Filter (multi-child only) */}
      {isMultiChild && (
        <div className="flex gap-2 mb-4 overflow-x-auto">
          <button
            onClick={() => setSelectedBabyIndex(null)}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
              selectedBabyIndex === null
                ? 'bg-pp-orange text-white'
                : 'glass-effect text-white/70 hover:text-white'
            }`}
          >
            {t('statsPage.all')}
          </button>
          {babies.map((baby) => (
            <button
              key={baby.index}
              onClick={() => setSelectedBabyIndex(baby.index)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                selectedBabyIndex === baby.index
                  ? 'bg-pp-orange text-white'
                  : 'glass-effect text-white/70 hover:text-white'
              }`}
            >
              {baby.name}
            </button>
          ))}
        </div>
      )}

      {/* Custom Date Picker Modal */}
      {showDatePicker && createPortal(
        <>
          <div className="bottom-sheet-backdrop" onClick={() => setShowDatePicker(false)} />
          <div className="bottom-sheet-container">
            <div className="bottom-sheet-modal">
              <div className="bottom-sheet-header">
                <h2>{t('statsPage.customRange.title')}</h2>
                <button
                  onClick={() => setShowDatePicker(false)}
                  className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30"
                >
                  <ChevronDown className="w-5 h-5" />
                </button>
              </div>
              <div className="bottom-sheet-content space-y-4">
                <div>
                  <label className="block text-white/70 text-sm mb-2">{t('statsPage.customRange.from')}</label>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    max={customEndDate}
                    className="w-full py-3 px-4 bg-white/10 rounded-[10px] text-white outline-none focus:ring-2 focus:ring-pp-orange"
                  />
                </div>
                <div>
                  <label className="block text-white/70 text-sm mb-2">{t('statsPage.customRange.to')}</label>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    min={customStartDate}
                    max={new Date().toISOString().split('T')[0]}
                    className="w-full py-3 px-4 bg-white/10 rounded-[10px] text-white outline-none focus:ring-2 focus:ring-pp-orange"
                  />
                </div>
                <p className="text-white/50 text-xs">
                  {t('statsPage.customRange.maxRange')}
                </p>
              </div>
              <div className="bottom-sheet-footer">
                <button
                  onClick={() => {
                    setShowDatePicker(false);
                    fetchStats();
                  }}
                  className="w-full py-4 bg-pp-orange text-white font-semibold rounded-[15px]"
                >
                  {t('statsPage.customRange.apply')}
                </button>
              </div>
            </div>
          </div>
        </>,
        document.body
      )}

      {/* Stats Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-white" />
        </div>
      ) : (
        <div className="pb-4">
          <BabyCareStatsTimeline
            entries={filteredEntries}
            selectedDays={selectedRange}
            customStartDate={customStartDate}
            customEndDate={customEndDate}
          />

          {/* Charts Section */}
          <BabyCareCharts
            entries={filteredEntries}
            selectedDays={selectedRange}
            customStartDate={customStartDate}
          />
        </div>
      )}
    </>
  );
}
